import { supabase } from '../../lib/supabase';

const LOCATION_TTL = 60 * 60 * 1000; // 60 minutos - TTL para ubicación del dispositivo
const SESSION_STORAGE_KEY = 'user_location_state';

/**
 * Lee el estado completo desde sessionStorage
 * @returns {Object|null} Estado parseado o null si no existe
 */
export const getLocationState = () => {
  const stateStr = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!stateStr) return null;
  try {
    return JSON.parse(stateStr);
  } catch (e) {
    console.error('[getLocationState] Error parsing state:', e);
    return null;
  }
};

/**
 * Guarda el estado completo en sessionStorage
 * @param {Object} state - Estado a guardar
 */
export const setLocationState = (state) => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('[setLocationState] Error saving state:', e);
  }
};

/**
 * Verifica si el navegador soporta geolocalización
 * @returns {boolean} True si soporta geolocalización
 */
export const isGeolocationSupported = () => {
  return 'geolocation' in navigator;
};

/**
 * Obtiene el estado actual de permisos de geolocalización
 * @returns {Promise<string>} Estado de permisos: 'granted', 'denied', 'prompt', o 'unsupported'
 */
export const getGeolocationPermissionStatus = async () => {
  if (!isGeolocationSupported()) {
    return 'unsupported';
  }
  
  // Verificar si la API de permisos está disponible (no funciona en todos los navegadores)
  if ('permissions' in navigator) {
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state;
    } catch (e) {
      console.log('[getGeolocationPermissionStatus] Permissions API not available');
    }
  }
  
  return 'prompt';
};

/**
 * Obtiene la ubicación real del dispositivo usando Geolocation API
 * Verifica si el cache en `current` es válido (menos de 60 minutos)
 * Mejorado para manejar permisos en iOS y Safari
 * @param {boolean} forceRefresh - Forzar actualización sin usar cache
 * @returns {Promise<Object>} Ubicación actual del dispositivo {lat, lng, timestamp}
 */
export const getCurrentDeviceLocation = async (forceRefresh = false) => {
  // Verificar soporte de geolocalización
  if (!isGeolocationSupported()) {
    throw new Error('Geolocalización no soportada en este navegador');
  }
  
  const state = getLocationState();
  const now = Date.now();
  
  // Verificar si existe cache válido (sino se fuerza actualización)
  if (!forceRefresh && state?.current?.timestamp) {
    const age = now - state.current.timestamp;
    if (age < LOCATION_TTL) {
      console.log('[getCurrentDeviceLocation] Reusing cached device location');
      return state.current;
    }
  }
  
  // Solicitar nueva ubicación con opciones optimizadas para iOS/Safari
  console.log('[getCurrentDeviceLocation] Requesting new device location');
  
  const position = await new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      resolve,
      (error) => {
        console.error('[getCurrentDeviceLocation] Geolocation error:', error);
        // Crear error con nombre específico para manejo en UI
        const geoError = new Error(error.message);
        geoError.name = 'GeolocationPositionError';
        geoError.code = error.code;
        geoError.PERMISSION_DENIED = 1;
        geoError.POSITION_UNAVAILABLE = 2;
        geoError.TIMEOUT = 3;
        reject(geoError);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000, // Aumentado para iOS
        maximumAge: 0 // Forzar ubicación fresca
      }
    );
  });
  
  const { latitude, longitude } = position.coords;
  const newCurrent = {
    lat: latitude,
    lng: longitude,
    timestamp: now
  };
  
  // Actualizar estado
  const newState = {
    ...state,
    current: newCurrent
  };
  setLocationState(newState);
  
  return newCurrent;
};

/**
 * Lee el estado desde sessionStorage y determina qué ubicación está activa
 * @returns {Object|null} Ubicación activa (current o explorer) o null si no hay estado
 */
export const getActiveLocation = () => {
  const state = getLocationState();
  if (!state) return null;
  
  if (state.activeSource === 'explorer' && state.explorer) {
    return state.explorer;
  }
  
  return state.current;
};

/**
 * Guarda la ubicación manual seleccionada por el usuario
 * @param {number} lat - Latitud
 * @param {number} lng - Longitud
 * @returns {Object} Estado actualizado
 */
export const setExplorerLocation = (lat, lng) => {
  const state = getLocationState() || {};
  const now = Date.now();
  
  const newState = {
    ...state,
    explorer: {
      lat,
      lng,
      timestamp: now
    },
    explorerActive: true,
    activeSource: 'explorer'
  };
  
  setLocationState(newState);
  return newState;
};

/**
 * Desactiva el modo Explorer
 * @returns {Object|null} Estado actualizado o null si no existe
 */
export const disableExplorerMode = () => {
  const state = getLocationState();
  if (!state) return null;
  
  const newState = {
    ...state,
    explorerActive: false,
    activeSource: 'device'
  };
  
  setLocationState(newState);
  return newState;
};

/**
 * Sincroniza la ubicación activa a la tabla user_geo
 * Usa formato PostGIS geography con SRID=4326
 * Implementa throttling para evitar escrituras innecesarias
 * @param {Object} location - Ubicación a sincronizar {lat, lng}
 * @param {string} userId - ID del usuario
 */
export const syncUserGeo = async (location, userId) => {
  if (!location || !userId) {
    console.log('[syncUserGeo] Missing location or userId');
    return;
  }
  
  const state = getLocationState();
  const now = Date.now();
  
  // Verificar si ya se sincronizó recientemente (30 segundos)
  if (state?.lastUserGeoSync && (now - state.lastUserGeoSync < 30000)) {
    console.log('[syncUserGeo] Synced recently, skipping');
    return;
  }
  
  try {
    const { error } = await supabase.rpc('update_user_geo', {
      lat_param: location.lat,
      lng_param: location.lng
    });
    
    if (error) {
      console.error('[syncUserGeo] Error syncing to user_geo:', error);
      throw error;
    }
    
    console.log('[syncUserGeo] Successfully synced to user_geo');
    
    // Actualizar timestamp de último sync
    const newState = {
      ...state,
      lastUserGeoSync: now
    };
    setLocationState(newState);
    
  } catch (e) {
    console.error('[syncUserGeo] Exception:', e);
    throw e;
  }
};
